/**
 * ToolsSidebar — right-hand dock for the writing tools (Feedback, Supervisor,
 * Coverage, The Coach). Mirrors the left Resources sidebar: Tabs or Stack
 * modes, drag-to-reorder, collapse to an icon rail, resizable. Layout prefs
 * sync to the account (right_panel_prefs) so they follow the user.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reorder } from "framer-motion";
import { ChevronLeft, ChevronRight, Plus, NotebookPen, ClipboardCheck, ScrollText, Compass } from "lucide-react";
import { DraggableStackSection } from "@/components/editor/ResourcesSidebar";

const TABS = [
  { id: "feedback", label: "Notepad", icon: NotebookPen },
  { id: "supervisor", label: "Supervisor", icon: ClipboardCheck },
  { id: "coverage", label: "Coverage", icon: ScrollText },
  { id: "coach", label: "Coach", icon: Compass },
];
const TABS_BY_ID = Object.fromEntries(TABS.map((t) => [t.id, t]));

const MIN_W = 300;
const MAX_W = 640;
const clampW = (w) => Math.max(MIN_W, Math.min(MAX_W, Math.round(w)));

const STACK_KEY = "screenplay_tools_stack_config";
const ORDER_KEY = "screenplay_tools_tab_order";
const STACK_DEFAULT = {
  feedback: { visible: true, minimized: false },
  supervisor: { visible: true, minimized: false },
  coverage: { visible: true, minimized: false },
  coach: { visible: true, minimized: false },
};

function loadJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}
function saveJson(key, v) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore */ }
}

function normalizeOrder(raw, allowed) {
  const base = Array.isArray(raw) ? raw.filter((id) => allowed.includes(id)) : [];
  for (const id of allowed) if (!base.includes(id)) base.push(id);
  return base;
}

export function ToolsSidebar({ tabs, activeTab, onActiveTab, collapsed, onToggle, renderPanel, initialWidth = 420, onWidthCommit }) {
  const { user } = useAuth();
  const serverPrefs = user?.right_panel_prefs || null;
  const [order, setOrder] = useState(() => normalizeOrder(serverPrefs?.order || loadJson(ORDER_KEY, null), tabs));
  const [stackMode, setStackMode] = useState(() => !!serverPrefs?.stackMode);
  const [stackCfg, setStackCfg] = useState(() => ({ ...STACK_DEFAULT, ...(serverPrefs?.stackCfg || loadJson(STACK_KEY, {})) }));
  const [addOpen, setAddOpen] = useState(false);

  // Remember the layout (tab, stack setup, order, collapsed) per user — synced across devices.
  const snapRef = useRef(null);
  useEffect(() => {
    const prefs = { activeTab, stackMode, stackCfg, order, collapsed };
    const snap = JSON.stringify(prefs);
    if (snapRef.current === null) { snapRef.current = snap; return; }
    if (snap === snapRef.current) return undefined;
    snapRef.current = snap;
    const t = setTimeout(() => { api.put("/auth/me", { right_panel_prefs: prefs }).catch(() => {}); }, 800);
    return () => clearTimeout(t);
  }, [activeTab, stackMode, stackCfg, order, collapsed]);

  // Opening a tool while in stack mode makes sure its section is on screen.
  useEffect(() => {
    if (!stackMode || collapsed) return;
    setStackCfg((prev) => {
      const cur = prev[activeTab];
      if (cur?.visible && !cur?.minimized) return prev;
      const next = { ...prev, [activeTab]: { visible: true, minimized: false } };
      saveJson(STACK_KEY, next);
      return next;
    });
  }, [activeTab, stackMode, collapsed]);

  // Width — resize handle sits on the LEFT edge of this right-docked sidebar.
  const [width, setWidth] = useState(() => clampW(initialWidth || 420));
  const [dragging, setDragging] = useState(false);
  const dragWRef = useRef(null);
  useEffect(() => { if (!dragging) setWidth(clampW(initialWidth || 420)); }, [initialWidth, dragging]);

  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    setDragging(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    const onMove = (ev) => {
      const w = clampW(startW - (ev.clientX - startX));
      dragWRef.current = w;
      setWidth(w);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setDragging(false);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      if (dragWRef.current != null && onWidthCommit) onWidthCommit(dragWRef.current);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const orderedTabs = normalizeOrder(order, tabs).map((id) => TABS_BY_ID[id]).filter(Boolean);
  const active = tabs.includes(activeTab) ? activeTab : tabs[0];

  const updateStack = (sectionId, patch) => {
    setStackCfg((prev) => {
      const next = { ...prev, [sectionId]: { ...prev[sectionId], ...patch } };
      saveJson(STACK_KEY, next);
      return next;
    });
  };

  const handleReorder = (newVisibleTabs) => {
    const visibleIds = newVisibleTabs.map((t) => t.id);
    const nextOrder = [...visibleIds, ...order.filter((id) => !visibleIds.includes(id))];
    setOrder(nextOrder);
    saveJson(ORDER_KEY, nextOrder);
  };

  if (collapsed) {
    return (
      <aside className="w-12 shrink-0 bg-[var(--sidebar-bg)] border-l border-[var(--line)] flex flex-col items-center py-4 gap-2" data-testid="tools-sidebar-collapsed">
        <button
          onClick={onToggle}
          className="p-2 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]"
          data-testid="tools-sidebar-expand-btn"
          title="Expand tools"
        >
          <ChevronLeft size={18} strokeWidth={1.5} />
        </button>
        {orderedTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => { onActiveTab(t.id); onToggle(); }}
              className="p-2 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]"
              data-testid={`tools-tab-collapsed-${t.id}`}
              title={t.label}
            >
              <Icon size={18} strokeWidth={1.5} />
            </button>
          );
        })}
      </aside>
    );
  }

  const visibleOrderedTabs = orderedTabs.filter((t) => stackCfg[t.id]?.visible);
  const hiddenStackSections = orderedTabs.filter((t) => !stackCfg[t.id]?.visible);

  return (
    <aside
      className="relative shrink-0 bg-[var(--sidebar-bg)] border-l border-[var(--line)] flex flex-col h-full"
      style={{ width }}
      data-testid="tools-sidebar"
    >
      <div className="flex items-center justify-between px-4 h-12 border-b border-[var(--line)] shrink-0">
        <span className="font-display text-base text-[var(--ink-primary)]">Tools</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setStackMode((s) => !s)}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${stackMode ? "bg-[var(--warm-accent)] text-[var(--ink-primary)]" : "text-[var(--ink-muted)] hover:bg-[var(--warm-accent)]"}`}
            data-testid="tools-sidebar-stack-toggle"
            title="Stack all sections (drag to reorder)"
          >
            {stackMode ? "Tabs" : "Stack"}
          </button>
          <button
            onClick={onToggle}
            className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]"
            data-testid="tools-sidebar-collapse-btn"
            title="Collapse"
          >
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {!stackMode && (
        <div className="flex border-b border-[var(--line)] text-[11px] tracking-wider uppercase shrink-0">
          {orderedTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => onActiveTab(t.id)}
              className={`flex-1 py-2.5 transition-colors ${active === t.id ? "text-[var(--brand)] border-b-2 border-[var(--brand)] -mb-px font-medium" : "text-[var(--ink-muted)] hover:text-[var(--ink-secondary)]"}`}
              data-testid={`tools-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {!stackMode ? (
        // Keep every panel mounted so chats & reports survive tab switches.
        <div className="flex-1 min-h-0" data-testid="tools-sidebar-content">
          {orderedTabs.map((t) => (
            <div key={t.id} className={active === t.id ? "h-full min-h-0 flex flex-col" : "hidden"}>
              {renderPanel(t.id)}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto" data-testid="tools-sidebar-content">
          {hiddenStackSections.length > 0 && (
            <div className="p-3 relative border-b border-[var(--line)]">
              <button
                onClick={() => setAddOpen((o) => !o)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-wider rounded border border-dashed border-[var(--line-strong)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"
                data-testid="tools-stack-add-section-btn"
              >
                <Plus size={14} strokeWidth={1.5} /> Add a section
              </button>
              {addOpen && (
                <div className="absolute left-3 right-3 top-full mt-1 bg-white border border-[var(--line)] rounded shadow-lg z-40" data-testid="tools-stack-add-menu">
                  {hiddenStackSections.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => { updateStack(t.id, { visible: true, minimized: false }); setAddOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--warm-accent)]"
                      data-testid={`tools-stack-add-${t.id}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <Reorder.Group axis="y" values={visibleOrderedTabs} onReorder={handleReorder} className="divide-y divide-[var(--line)] list-none m-0 p-0" as="div">
            {visibleOrderedTabs.map((t) => (
              <DraggableStackSection
                key={t.id}
                tab={t}
                minimized={!!stackCfg[t.id]?.minimized}
                onToggleMinimize={() => updateStack(t.id, { minimized: !stackCfg[t.id]?.minimized })}
                onRemove={() => updateStack(t.id, { visible: false, minimized: false })}
              >
                <div className="h-[440px] flex flex-col min-h-0 border-t border-[var(--line)] bg-[var(--paper)]">
                  {renderPanel(t.id)}
                </div>
              </DraggableStackSection>
            ))}
          </Reorder.Group>
        </div>
      )}

      <div
        onPointerDown={startResize}
        className={`absolute top-0 left-0 z-20 h-full w-1.5 cursor-col-resize transition-colors ${dragging ? "bg-[var(--brand)]" : "hover:bg-[var(--brand-soft)]"}`}
        data-testid="tools-sidebar-resize-handle"
        title="Drag to resize"
      />
    </aside>
  );
}
